import { useEffect, useMemo, useState } from 'react';
import { StatsQuality } from './StatsQuality';
import { ApiError, type AgentRow, type Api, type Stats, agentTitle } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';

const DAYS = [7, 30, 90] as const;
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
const dur = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

/** Every day in the window (days without calls are shown as empty, not skipped). */
const dayList = (days: number, tz: string): string[] => {
  const out: string[] = [];
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  for (let i = days - 1; i >= 0; i -= 1) out.push(fmt.format(new Date(Date.now() - i * 86_400_000)));
  return out;
};

/**
 * Business statistics: what the agents did and what it brought (real calls only). Filters sit in one row above;
 * every chart has a hover read-out and the daily chart a table view.
 */
type Go = (route: 'agent' | 'call', params?: Record<string, string>) => void;

export const StatsPage = ({ api, t, go }: { api: Api; t: T; go: Go }) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [days, setDays] = useState<(typeof DAYS)[number]>(30);
  const [agentId, setAgentId] = useState('');
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound'>('');
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [data, setData] = useState<Stats>();
  const [error, setError] = useState<string>();
  const [asTable, setAsTable] = useState(false);

  useEffect(() => void api.agents().then((r) => setAgents(r.items)).catch(() => undefined), [api]);
  useEffect(() => {
    setData(undefined);
    setError(undefined);
    api
      .stats({ days, tz, ...(agentId ? { agentId } : {}), ...(direction ? { direction } : {}) })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, days, agentId, direction, tz]);

  const s = data?.totals;
  return (
    <section className="stats">
      <header className="page-head">
        <div>
          <h1>{t('stats.title')}</h1>
          <p className="muted">{t('stats.subtitle')}</p>
        </div>
      </header>
      <div className="stats-filters">
        <div className="segmented" role="group" aria-label={t('stats.period')}>
          {DAYS.map((d) => <button key={d} type="button" className={days === d ? 'on' : ''} onClick={() => setDays(d)}>{t('stats.lastDays', { n: d })}</button>)}
        </div>
        <select value={agentId} aria-label={t('stats.agent')} onChange={(e) => setAgentId(e.target.value)}>
          <option value="">{t('stats.allAgents')}</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
        </select>
        <select value={direction} aria-label={t('templates.direction')} onChange={(e) => setDirection(e.target.value as typeof direction)}>
          <option value="">{t('stats.allDirections')}</option>
          <option value="outbound">{t('direction.outbound')}</option>
          <option value="inbound">{t('direction.inbound')}</option>
        </select>
      </div>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}
      {!data && !error && <p className="muted">{t('boot.loading')}</p>}
      {s && s.calls === 0 && <div className="empty"><p>{t('stats.empty')}</p></div>}
      {s && s.calls > 0 && (
        <>
          <div className="stat-tiles">
            <Tile label={t('stats.kpi.calls')} value={s.calls} note={t('stats.kpi.answered', { p: pct(s.answered, s.calls) })} />
            <Tile label={t('stats.kpi.talked')} value={s.talked} note={t('stats.kpi.avg', { d: dur(s.avgSeconds) })} />
            <Tile label={t('stats.kpi.minutes')} value={s.minutes.toLocaleString()} note={t('stats.kpi.minutesNote')} />
            <Tile label={t('stats.kpi.hot')} value={s.hot} note={t('stats.kpi.hotNote', { p: pct(s.hot, s.talked), warm: s.warm })} />
            <Tile label={t('stats.kpi.callbacks')} value={s.callbacks} note={t('stats.kpi.callbacksNote')} />
            <Tile label={t('stats.kpi.optOuts')} value={s.optOuts} note={t('stats.kpi.optOutsNote', { p: pct(s.optOuts, s.answered) })} />
            <Tile label={t('stats.kpi.machine')} value={s.machine} note={t('stats.kpi.machineNote')} />
            <Tile label={t('stats.kpi.mood')} value={`${pct(s.positive, s.positive + s.negative + Math.max(0, s.talked - s.positive - s.negative))}%`} note={t('stats.kpi.moodNote', { n: s.negative })} />
          </div>

          <UnansweredCard api={api} t={t} go={go} rows={data.unanswered} />

          <UsageCard t={t} s={s} usage={data.usage} />

          <div className="card chart-card">
            <div className="row-between">
              <div>
                <h2>{t('stats.daily.title')}</h2>
                <p className="muted small">{t('stats.daily.hint')}</p>
              </div>
              <button type="button" className="link" onClick={() => setAsTable((x) => !x)}>{asTable ? t('stats.showChart') : t('stats.showTable')}</button>
            </div>
            {asTable ? <DailyTable t={t} data={data} days={dayList(days, tz)} /> : <DailyChart t={t} data={data} days={dayList(days, tz)} />}
          </div>

          <div className="two-col">
            {direction !== 'inbound' && (
              <div className="card chart-card">
                <h2>{t('stats.funnel.title')}</h2>
                <p className="muted small">{t('stats.funnel.hint')}</p>
                <Funnel t={t} steps={[
                  { label: t('stats.funnel.dialed'), n: s.calls },
                  { label: t('stats.funnel.answered'), n: s.answered },
                  { label: t('stats.funnel.talked'), n: s.talked },
                  { label: t('stats.funnel.interested'), n: s.hot + s.warm },
                  { label: t('stats.funnel.next'), n: s.callbacks },
                ]} />
              </div>
            )}
            <div className="card chart-card">
              <h2>{t('stats.heat.title')}</h2>
              <p className="muted small">{t('stats.heat.hint')}</p>
              <Heatmap t={t} cells={data.heat} />
            </div>
          </div>

          <div className="card">
            <h2>{t('stats.agents.title')}</h2>
            <div className="table-wrap">
              <table className="table">
                <thead><tr>
                  <th>{t('stats.agent')}</th><th className="num">{t('stats.kpi.calls')}</th><th className="num">{t('stats.col.answered')}</th><th className="num">{t('stats.kpi.minutes')}</th>
                  <th className="num">{t('stats.col.avg')}</th><th className="num">{t('stats.kpi.hot')}</th><th className="num">{t('stats.kpi.callbacks')}</th><th className="num">{t('stats.kpi.optOuts')}</th>
                </tr></thead>
                <tbody>
                  {data.agents.map((a) => (
                    <tr key={a.agentId}>
                      <td>{a.name}</td><td className="num">{a.calls}</td><td className="num">{pct(a.answered, a.calls)}%</td><td className="num">{a.minutes.toLocaleString()}</td>
                      <td className="num">{dur(a.avgSeconds)}</td><td className="num">{a.hot}</td><td className="num">{a.callbacks}</td><td className="num">{a.optOuts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {(data.offerings?.length ?? 0) > 0 && <OfferingsCard t={t} rows={data.offerings!} agents={data.agents} />}

          <div className="two-col">
            <EndingsCard t={t} rows={data.endings} />
            <CallbacksCard t={t} go={go} c={data.callbacks} />
          </div>

          {data.campaigns.length > 0 && <CampaignsCard t={t} rows={data.campaigns} usage={data.usage} />}

          {data.topics.length > 0 && (
            <div className="card chart-card">
              <h2>{t('stats.topics.title')}</h2>
              <p className="muted small">{t('stats.topics.hint')}</p>
              <Funnel t={t} steps={data.topics.map((x) => ({ label: x.topic, n: x.n }))} plain />
            </div>
          )}
        </>
      )}
      <StatsQuality api={api} t={t} days={days} agentId={agentId} direction={direction} onOpenCall={(id) => go('call', { id })} />
    </section>
  );
};

const Tile = ({ label, value, note }: { label: string; value: string | number; note: string }) => (
  <div className="stat-tile">
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-note">{note}</div>
  </div>
);

type Seg = { key: 'hot' | 'warm' | 'cold' | 'notReached'; n: number };
const SEGS = ['hot', 'warm', 'cold', 'notReached'] as const;

const DailyChart = ({ t, data, days }: { t: T; data: Stats; days: string[] }) => {
  const [hover, setHover] = useState<number | null>(null);
  const byDay = useMemo(() => new Map(data.daily.map((d) => [d.day, d])), [data]);
  const rows = days.map((day) => byDay.get(day) ?? { day, calls: 0, hot: 0, warm: 0, cold: 0, notReached: 0 });
  const max = Math.max(1, ...rows.map((r) => r.calls));
  const W = 720, H = 220, L = 32, B = 22, T0 = 8;
  const bw = (W - L) / rows.length;
  const bar = Math.max(2, Math.min(28, bw - 2));
  const y = (v: number) => T0 + (H - T0 - B) * (1 - v / max);
  const ticks = [0, Math.ceil(max / 2), max];
  const every = Math.ceil(rows.length / 8);
  const h = hover !== null ? rows[hover]! : null;
  return (
    <div className="chart" onMouseLeave={() => setHover(null)}>
      <Legend t={t} />
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('stats.daily.title')}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W} y1={y(v)} y2={y(v)} className="grid" />
            <text x={L - 6} y={y(v) + 4} className="tick" textAnchor="end">{v}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = L + i * bw + (bw - bar) / 2;
          let acc = 0;
          const segs: Seg[] = SEGS.map((k) => ({ key: k, n: r[k] }));
          return (
            <g key={r.day}>
              {segs.filter((sg) => sg.n > 0).map((sg, j, all) => {
                const y1 = y(acc + sg.n);
                const hgt = y(acc) - y1;
                acc += sg.n;
                const top = j === all.length - 1;
                return <rect key={sg.key} x={x} y={y1 + 1} width={bar} height={Math.max(0, hgt - 1)} rx={top ? Math.min(4, bar / 2) : 0} className={`seg ${sg.key}`} />;
              })}
              {i % every === 0 && <text x={x + bar / 2} y={H - 6} className="tick" textAnchor="middle">{r.day.slice(5).replace('-', '.')}</text>}
              <rect x={L + i * bw} y={0} width={bw} height={H - B} fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
        <line x1={L} x2={W} y1={H - B} y2={H - B} className="axis" />
      </svg>
      {h && (
        <div className="viz-tip" style={{ insetInlineStart: `${Math.min(80, ((hover! + 0.5) / rows.length) * 100)}%` }}>
          <strong>{h.day}</strong>
          <div>{t('stats.kpi.calls')}: {h.calls}</div>
          {SEGS.map((k) => <div key={k}><span className={`swatch ${k}`} />{t(`stats.seg.${k}`)}: {h[k]}</div>)}
          {h.calls - h.hot - h.warm - h.cold - h.notReached > 0 && <div className="muted">{t('stats.seg.unscored')}: {h.calls - h.hot - h.warm - h.cold - h.notReached}</div>}
        </div>
      )}
    </div>
  );
};

const Legend = ({ t }: { t: T }) => (
  <div className="viz-legend">
    {SEGS.map((k) => <span key={k}><span className={`swatch ${k}`} />{t(`stats.seg.${k}`)}</span>)}
  </div>
);

const DailyTable = ({ t, data, days }: { t: T; data: Stats; days: string[] }) => {
  const byDay = new Map(data.daily.map((d) => [d.day, d]));
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>{t('stats.col.day')}</th><th className="num">{t('stats.kpi.calls')}</th>{SEGS.map((k) => <th key={k} className="num">{t(`stats.seg.${k}`)}</th>)}</tr></thead>
        <tbody>
          {days.filter((d) => byDay.has(d)).map((d) => {
            const r = byDay.get(d)!;
            return <tr key={d}><td>{d}</td><td className="num">{r.calls}</td>{SEGS.map((k) => <td key={k} className="num">{r[k]}</td>)}</tr>;
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Horizontal bars, one hue; each step shows its count and share of the first. */
const Funnel = ({ t, steps, plain }: { t: T; steps: Array<{ label: string; n: number }>; plain?: boolean }) => {
  const max = Math.max(1, ...steps.map((s) => s.n));
  return (
    <div className="hbars">
      {steps.map((s, i) => (
        <div key={i} className="hbar" title={plain || i === 0 ? `${s.label}: ${s.n}` : `${s.label}: ${s.n} (${t('stats.funnel.ofFirst', { p: pct(s.n, steps[0]!.n) })})`}>
          <span className="hbar-label">{s.label}</span>
          <span className="hbar-track"><span className="hbar-fill" style={{ width: `${(s.n / max) * 100}%` }} /></span>
          <span className="hbar-value">{s.n}{!plain && i > 0 && <span className="muted"> · {pct(s.n, steps[0]!.n)}%</span>}</span>
        </div>
      ))}
    </div>
  );
};

/** Weekday × hour: call count (sequential, one hue); the hover read-out adds how many were answered. */
const Heatmap = ({ t, cells }: { t: T; cells: Stats['heat'] }) => {
  const [hover, setHover] = useState<{ dow: number; hour: number } | null>(null);
  const at = new Map(cells.map((c) => [`${c.dow}-${c.hour}`, c]));
  const max = Math.max(1, ...cells.map((c) => c.calls));
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const step = (n: number) => (n === 0 ? 0 : Math.min(6, 1 + Math.floor((n / max) * 5.999)));
  const cur = hover ? at.get(`${hover.dow}-${hover.hour}`) : undefined;
  return (
    <div className="heat" onMouseLeave={() => setHover(null)}>
      <div className="heat-grid" role="img" aria-label={t('stats.heat.title')}>
        <span />
        {hours.map((h) => <span key={h} className="heat-h">{h % 3 === 0 ? h : ''}</span>)}
        {[1, 2, 3, 4, 5, 6, 7].map((d) => (
          <FragmentRow key={d} label={t(`stats.dow.${d}`)}>
            {hours.map((h) => {
              const c = at.get(`${d}-${h}`);
              return <span key={h} className={`heat-cell s${step(c?.calls ?? 0)}`} onMouseEnter={() => setHover({ dow: d, hour: h })} />;
            })}
          </FragmentRow>
        ))}
      </div>
      <div className="heat-read muted small">
        {hover ? `${t(`stats.dow.${hover.dow}`)} ${String(hover.hour).padStart(2, '0')}:00 — ${t('stats.heat.read', { n: cur?.calls ?? 0, a: cur?.answered ?? 0 })}` : t('stats.heat.hover')}
      </div>
    </div>
  );
};

const FragmentRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <span className="heat-d">{label}</span>
    {children}
  </>
);

/**
 * What the calls cost in the business's own currency of the platform (credits), computed the way Panaryus bills:
 * a connect per call that went live plus whole minutes rounded up. Plan minutes and remaining credits come from
 * Panaryus; without them only the billable minutes are shown (never a guessed price).
 */
const UsageCard = ({ t, s, usage }: { t: T; s: Stats['totals']; usage: Stats['usage'] }) => {
  const spent = usage ? s.connects * usage.connectCredits + s.billableMinutes * usage.minuteCredits : null;
  const per = (n: number) => (spent !== null && n > 0 ? Math.round(spent / n) : null);
  const limit = usage?.minutesLimit ?? null;
  const unlimited = limit === null || limit < 0;
  return (
    <div className="card chart-card usage-card">
      <h2>{t('stats.usage.title')}</h2>
      <p className="muted small">{usage ? t('stats.usage.rates', { c: usage.connectCredits, m: usage.minuteCredits }) : t('stats.usage.unavailable')}</p>
      <div className="stat-tiles">
        <Tile label={t('stats.usage.billable')} value={s.billableMinutes} note={t('stats.usage.billableNote', { n: s.connects })} />
        {spent !== null && <Tile label={t('stats.usage.spent')} value={spent.toLocaleString()} note={t('stats.usage.spentNote')} />}
        {spent !== null && <Tile label={t('stats.usage.perHot')} value={per(s.hot) ?? '—'} note={t('stats.usage.perHotNote')} />}
        {spent !== null && <Tile label={t('stats.usage.perCallback')} value={per(s.callbacks) ?? '—'} note={t('stats.usage.perCallbackNote')} />}
        {usage && <Tile label={t('stats.usage.credits')} value={usage.creditsUnlimited ? '∞' : (usage.creditsRemaining ?? 0).toLocaleString()} note={t('stats.usage.creditsNote')} />}
      </div>
      {usage && (
        <div className="plan-meter">
          <div className="row-between small">
            <span>{t('stats.usage.plan', { plan: usage.plan ?? '—' })}</span>
            <span className="num">{unlimited ? t('stats.usage.unlimited', { used: usage.minutesUsed }) : t('stats.usage.minutes', { used: usage.minutesUsed, limit: limit! })}</span>
          </div>
          {!unlimited && (
            <div className="hbar-track" role="meter" aria-valuemin={0} aria-valuemax={limit!} aria-valuenow={usage.minutesUsed}>
              <span className={`hbar-fill ${usage.minutesUsed / limit! >= 0.9 ? 'near' : ''}`} style={{ width: `${Math.min(100, (usage.minutesUsed / Math.max(1, limit!)) * 100)}%` }} />
            </div>
          )}
          {!unlimited && usage.minutesUsed / limit! >= 0.9 && <p className="banner warn small">{t('stats.usage.near')}</p>}
        </div>
      )}
    </div>
  );
};

/**
 * Campaign comparison: which list and agent brought the most interested customers for the credits spent.
 * "Best" is marked only with enough conversations (5+) to mean something.
 */
const CampaignsCard = ({ t, rows, usage }: { t: T; rows: Stats['campaigns']; usage: Stats['usage'] }) => {
  const credits = (r: Stats['campaigns'][number]) => (usage ? r.connects * usage.connectCredits + r.billableMinutes * usage.minuteCredits : null);
  const rate = (r: Stats['campaigns'][number]) => (r.talked > 0 ? r.hot / r.talked : 0);
  const eligible = rows.filter((r) => r.talked >= 5);
  const best = eligible.length > 1 ? eligible.reduce((a, b) => (rate(b) > rate(a) ? b : a)) : null;
  const maxRate = Math.max(0.0001, ...rows.map(rate));
  return (
    <div className="card chart-card">
      <h2>{t('stats.campaigns.title')}</h2>
      <p className="muted small">{t('stats.campaigns.hint')}</p>
      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th>{t('stats.campaigns.name')}</th><th className="num">{t('stats.campaigns.list')}</th><th className="num">{t('stats.kpi.calls')}</th><th className="num">{t('stats.col.answered')}</th>
            <th>{t('stats.campaigns.hotRate')}</th><th className="num">{t('stats.seg.warm')}</th><th className="num">{t('stats.kpi.callbacks')}</th><th className="num">{t('stats.kpi.optOuts')}</th>
            {usage && <><th className="num">{t('stats.campaigns.credits')}</th><th className="num">{t('stats.usage.perHot')}</th></>}
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const c = credits(r);
              return (
                <tr key={r.campaignId}>
                  <td>{r.name} <span className="muted small">· {t(`campaigns.status.${r.status}`) === `campaigns.status.${r.status}` ? r.status : t(`campaigns.status.${r.status}`)}</span>{best?.campaignId === r.campaignId && <span className="tag own best">{t('stats.campaigns.best')}</span>}</td>
                  <td className="num">{r.targetsDone}/{r.targets}</td>
                  <td className="num">{r.calls}</td>
                  <td className="num">{pct(r.answered, r.calls)}%</td>
                  <td><span className="rate-cell"><span className="hbar-track"><span className="hbar-fill" style={{ width: `${(rate(r) / maxRate) * 100}%` }} /></span><span className="num">{r.hot} · {pct(r.hot, r.talked)}%</span></span></td>
                  <td className="num">{r.warm}</td>
                  <td className="num">{r.callbacks}</td>
                  <td className="num">{r.optOuts}</td>
                  {usage && <><td className="num">{c?.toLocaleString()}</td><td className="num">{c !== null && r.hot > 0 ? Math.round(c / r.hot) : '—'}</td></>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * What callers asked and the agent could not answer — the most useful list for improving an agent. "Add the answer"
 * opens that question on the agent's Readiness tab (a new question joins the list first).
 */
const UnansweredCard = ({ api, t, go, rows }: { api: Api; t: T; go: Go; rows: Stats['unanswered'] }) => {
  // ADR-071: adding the answer edits the agent (centeros.agents); without it the list is read-only.
  const canAnswer = useCan()(PERM.agents);
  const [busy, setBusy] = useState<string | null>(null);
  const open = async (u: Stats['unanswered'][number]) => {
    setBusy(u.question);
    try {
      const questionId = u.questionId ?? (await api.addReadinessQuestion(u.agentId, { question: u.question, why: t('stats.unanswered.why', { n: u.calls }), impact: u.calls >= 3 ? 'high' : 'medium' })).questionId;
      go('agent', { id: u.agentId, tab: 'readiness', q: questionId });
    } catch {
      setBusy(null);
    }
  };
  const open_ = rows.filter((u) => !u.answered);
  return (
    <div className="card unanswered">
      <div className="row-between">
        <div>
          <h2>{t('stats.unanswered.title')}</h2>
          <p className="muted small">{t('stats.unanswered.hint')}</p>
        </div>
        {open_.length > 0 && <span className="tag warn">{t('stats.unanswered.open', { n: open_.length })}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="muted">{t('stats.unanswered.empty')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('stats.unanswered.question')}</th><th>{t('stats.agent')}</th><th className="num">{t('stats.unanswered.calls')}</th><th /></tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={`${u.agentId}|${u.questionId ?? u.question}`} className={u.answered ? 'muted' : ''}>
                  <td><strong>{u.question}</strong></td>
                  <td>{u.agentName}</td>
                  <td className="num">
                    <button type="button" className="link" title={t('stats.unanswered.lastCall')} onClick={() => go('call', { id: u.lastCallId })}>{u.calls}</button>
                  </td>
                  <td className="actions">
                    {u.answered ? <span className="ok-text">✓ {t('stats.unanswered.done')}</span> : canAnswer && (
                      <button type="button" className="primary small" disabled={busy !== null} onClick={() => void open(u)}>{busy === u.question ? '…' : t('stats.unanswered.add')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const STAGES = ['opening', 'discovery', 'pitch', 'objection', 'closing'] as const;

/** Where talked calls ended and how many of those the customer hung up — which part of the talk to improve. */
const EndingsCard = ({ t, rows }: { t: T; rows: Stats['endings'] }) => {
  const by = new Map(rows.map((r) => [r.stage, r]));
  const list = STAGES.map((st) => by.get(st) ?? { stage: st, calls: 0, byCustomer: 0, interested: 0 });
  const total = list.reduce((a, r) => a + r.calls, 0);
  const max = Math.max(1, ...list.map((r) => r.calls));
  // The stage where most customers hung up (the "leak"); closing is where a call is meant to end.
  const leak = [...list].filter((r) => r.stage !== 'closing' && r.byCustomer > 0).sort((a, b) => b.byCustomer - a.byCustomer)[0];
  return (
    <div className="card chart-card">
      <h2>{t('stats.endings.title')}</h2>
      <p className="muted small">{t('stats.endings.hint')}</p>
      {total === 0 ? <p className="muted">{t('stats.endings.empty')}</p> : (
        <>
          <div className="hbars">
            {list.map((r) => (
              <div key={r.stage} className="hbar" title={t('stats.endings.row', { n: r.calls, c: r.byCustomer, i: r.interested })}>
                <span className="hbar-label">{t(`stats.stage.${r.stage}`)}</span>
                <span className="hbar-track"><span className="hbar-fill" style={{ width: `${(r.calls / max) * 100}%` }} /></span>
                <span className="hbar-value">{r.calls}{r.byCustomer > 0 && <span className="muted"> · {t('stats.endings.hungUp', { n: r.byCustomer })}</span>}</span>
              </div>
            ))}
          </div>
          {leak && <p className="endings-advice">{t(`stats.endings.advice.${leak.stage}`, { n: leak.byCustomer })}</p>}
        </>
      )}
    </div>
  );
};

const CB_ROWS = ['reached', 'notReached', 'team', 'waiting', 'late', 'failed', 'manual'] as const;

/** Callback promises: were they kept on time, was the customer reached, and which need attention now. */
const CallbacksCard = ({ t, go, c }: { t: T; go: Go; c: Stats['callbacks'] }) => {
  const max = Math.max(1, ...CB_ROWS.map((k) => c[k]));
  const placed = c.reached + c.notReached;
  return (
    <div className="card chart-card">
      <h2>{t('stats.cb.title')}</h2>
      <p className="muted small">{t('stats.cb.hint')}</p>
      {c.promised === 0 ? <p className="muted">{t('stats.cb.empty')}</p> : (
        <>
          <p className="cb-summary">{t('stats.cb.summary', { n: c.promised, onTime: c.onTime, placed, p: pct(c.onTime, placed) })}</p>
          <div className="hbars">
            {CB_ROWS.filter((k) => c[k] > 0).map((k) => (
              <div key={k} className={`hbar cb-${k}`} title={t(`stats.cb.${k}.hint`)}>
                <span className="hbar-label">{k === 'late' || k === 'failed' ? '⚠ ' : ''}{t(`stats.cb.${k}`)}</span>
                <span className="hbar-track"><span className="hbar-fill" style={{ width: `${(c[k] / max) * 100}%` }} /></span>
                <span className="hbar-value">{c[k]}</span>
              </div>
            ))}
          </div>
          {c.problems.length > 0 && (
            <ul className="cb-problems">
              {c.problems.map((p) => (
                <li key={p.callId}>
                  <span>⚠ {p.agentName} · {p.at ? new Date(p.at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—'} · {p.state === 'late' ? t('stats.cb.late') : t(`stats.cb.error.${p.error ?? 'other'}`) === `stats.cb.error.${p.error ?? 'other'}` ? t('stats.cb.error.other') : t(`stats.cb.error.${p.error ?? 'other'}`)}</span>
                  <button type="button" className="link" onClick={() => go('call', { id: p.callId })}>{t('stats.cb.open')}</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

/** ADR-041: which products/services were presented and how customers took them (interest rate per item). */
const OfferingsCard = ({ t, rows, agents }: { t: T; rows: NonNullable<Stats['offerings']>; agents: Stats['agents'] }) => {
  const agentName = (id: string) => agents.find((a) => a.agentId === id)?.name ?? '—';
  return (
    <div className="card">
      <h2>{t('stats.offerings.title')}</h2>
      <p className="muted small">{t('stats.offerings.hint')}</p>
      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th>{t('stats.offerings.item')}</th><th>{t('stats.agent')}</th><th className="num">{t('stats.offerings.presented')}</th>
            <th>{t('stats.offerings.interest')}</th><th className="num">{t('stats.offerings.cold')}</th><th className="num">{t('stats.kpi.callbacks')}</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.agentId}|${r.offeringId}`}>
                <td><strong>{r.name}</strong></td>
                <td>{agentName(r.agentId)}</td>
                <td className="num">{r.presented}</td>
                <td><span className="rate-cell"><span className="hbar-track"><span className="hbar-fill" style={{ width: `${pct(r.interested, r.presented)}%` }} /></span><span className="num">{r.interested} · {pct(r.interested, r.presented)}%</span></span></td>
                <td className="num">{r.cold}</td>
                <td className="num">{r.callbacks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
